import { Check, ListFilter } from "lucide-react";
import { Filter } from "@/shared/lib/filter/filter";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Button } from "@/shared/components/ui/button";
import t from "@/lang/lang";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import {
  FulfillmentState,
  FulfillmentStateList,
} from "@/services/backend/relationships/types/fulfillment";
import { FulfillmentStateIcon } from "@/services/backend/relationships/components/fulfillment-state-icon";

export interface FulfillmentStateFilterProps {
  filter: Filter<FulfillmentState> | undefined;
  onFilterChange: (filter: Filter<FulfillmentState>) => void;
  hidden?: boolean;
  hideStates?: FulfillmentState[];
}

export function FulfillmentStateFilter({
  filter,
  onFilterChange,
  hidden = false,
  hideStates = [FulfillmentState.Zero],
}: FulfillmentStateFilterProps) {
  // This allows us to accept undefined filters,
  // which is common in list requests. This makes
  // it nicer to call the component.
  const filterVal = filter ?? { active: false, values: [] };
  // simplifies some code below
  const currentValues = filterVal.values ?? [];

  if (hidden) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm">
          <ListFilter />
          {t("Status")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0" align="start">
        <Command>
          <CommandInput placeholder={t("Status")} />
          <CommandList>
            <CommandEmpty className="py-3 text-center text-sm">
              {t("Status nicht gefunden")}
            </CommandEmpty>
            <CommandGroup>
              {FulfillmentStateList.filter((s) => !hideStates?.includes(s)).map(
                (fulfillmentState) => (
                  <CommandItem
                    key={fulfillmentState}
                    value={fulfillmentState}
                    onSelect={() => {
                      const values = currentValues.includes(fulfillmentState)
                        ? currentValues.filter((v) => v !== fulfillmentState)
                        : [...currentValues, fulfillmentState];
                      onFilterChange({
                        active: values.length > 0,
                        values,
                      });
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        currentValues.includes(fulfillmentState)
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                    <FulfillmentStateIcon state={fulfillmentState} />
                    <span className="whitespace-nowrap">
                      {t(fulfillmentState)}
                    </span>
                  </CommandItem>
                ),
              )}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
